import dayjs from 'dayjs';
import { ALERT_DAYS_CRITICAL, ALERT_DAYS_WARNING } from './constants';

// ===== DATE HELPERS =====
export const formatDate = (date) => {
  if (!date) return '—';
  return dayjs(date).format('DD/MM/YYYY');
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  return dayjs(date).format('DD/MM/YYYY HH:mm');
};

export const daysUntil = (date) => {
  if (!date) return null;
  return dayjs(date).diff(dayjs(), 'day');
};

export const getAlertLevel = (date) => {
  const days = daysUntil(date);
  if (days === null) return null;
  if (days < 0) return 'passed';
  if (days <= ALERT_DAYS_CRITICAL) return 'critical';
  if (days <= ALERT_DAYS_WARNING) return 'warning';
  return 'normal';
};

export const getAlertColor = (level) => {
  switch (level) {
    case 'critical': return '#C62828';
    case 'warning': return '#FF6F00';
    case 'passed': return '#1565C0';
    default: return '#757575';
  }
};

export const getAlertBgColor = (level) => {
  switch (level) {
    case 'critical': return '#FFEBEE';
    case 'warning': return '#FFF3E0';
    case 'passed': return '#E3F2FD';
    default: return '#F5F5F5';
  }
};

// ===== TENDER HELPERS =====
export const generateId = () => {
  return crypto.randomUUID ? crypto.randomUUID() : 
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
};

export const getNextSequentialId = (tenders) => {
  if (!tenders || tenders.length === 0) return 1;
  return Math.max(...tenders.map((t) => t.id || 0)) + 1;
};

export const isActiveTender = (tender) => {
  return !['won', 'lost', 'skipped'].includes(tender.category);
};

export const getCategoryFromTender = (tender) => {
  if (tender.win_status === 'זכייה') return 'won';
  if (tender.win_status === 'אי-זכייה') return 'lost';
  if (tender.status === 'הוגש') return 'submitted';
  return 'active';
};

// ===== STATISTICS HELPERS =====
export const calculateStats = (tenders) => {
  const now = dayjs();
  const activeTenders = tenders.filter(t => t.category === 'active');
  
  return {
    total: tenders.length,
    submitted: tenders.filter(t => t.category === 'submitted').length,
    skipped: tenders.filter(t => t.category === 'skipped').length,
    clarification: activeTenders.filter(t => {
      const d = daysUntil(t.clarification_date);
      return d !== null && d >= 0 && d <= ALERT_DAYS_WARNING;
    }).length,
    upcoming: activeTenders.filter(t => {
      const d = daysUntil(t.submission_date);
      return d !== null && d >= 0 && d <= ALERT_DAYS_WARNING;
    }).length,
    won: tenders.filter(t => t.category === 'won').length,
    lost: tenders.filter(t => t.category === 'lost').length,
    winRate: (() => {
      const decided = tenders.filter(t => ['won', 'lost'].includes(t.category));
      const wins = tenders.filter(t => t.category === 'won');
      return decided.length > 0 ? Math.round((wins.length / decided.length) * 100) : 0;
    })(),
  };
};

export const getAlerts = (tenders) => {
  const alerts = [];
  const activeTenders = tenders.filter(t => 
    t.category === 'active' || t.category === 'submitted'
  );

  activeTenders.forEach(tender => {
    // Check clarification date
    if (tender.clarification_date) {
      const days = daysUntil(tender.clarification_date);
      const level = getAlertLevel(tender.clarification_date);
      if (level === 'critical' || level === 'warning' || level === 'passed') {
        alerts.push({
          id: `${tender.rowKey}-clarification`,
          tenderId: tender.rowKey,
          tenderSubject: tender.subject,
          tenderNumber: tender.tender_number,
          type: 'שאלות הבהרה',
          date: tender.clarification_date,
          daysLeft: days,
          level,
        });
      }
    }

    // Check submission date
    if (tender.submission_date) {
      const days = daysUntil(tender.submission_date);
      const level = getAlertLevel(tender.submission_date);
      if (level === 'critical' || level === 'warning' || level === 'passed') {
        alerts.push({
          id: `${tender.rowKey}-submission`,
          tenderId: tender.rowKey,
          tenderSubject: tender.subject,
          tenderNumber: tender.tender_number,
          type: 'מועד הגשה',
          date: tender.submission_date,
          daysLeft: days,
          level,
        });
      }
    }
  });

  // Sort: critical first, then warning, then passed
  const order = { critical: 0, warning: 1, passed: 2 };
  return alerts.sort((a, b) => (order[a.level] || 99) - (order[b.level] || 99));
};

// ===== CHART DATA HELPERS =====
export const getWinRateOverTime = (tenders) => {
  const months = {};
  tenders.forEach(t => {
    if (t.category === 'won' || t.category === 'lost') {
      const month = dayjs(t.updated_at || t.created_at).format('YYYY-MM');
      if (!months[month]) months[month] = { wins: 0, total: 0 };
      months[month].total++;
      if (t.category === 'won') months[month].wins++;
    }
  });

  const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
  return {
    labels: sorted.map(([m]) => dayjs(m).format('MM/YYYY')),
    data: sorted.map(([, v]) => Math.round((v.wins / v.total) * 100)),
  };
};

export const getFieldDistribution = (tenders) => {
  const fields = {};
  tenders.forEach(t => {
    if (t.field) {
      fields[t.field] = (fields[t.field] || 0) + 1;
    }
  });
  return {
    labels: Object.keys(fields),
    data: Object.values(fields),
  };
};

export const getStatusDistribution = (tenders) => {
  const statuses = {};
  tenders.forEach(t => {
    if (t.status) {
      statuses[t.status] = (statuses[t.status] || 0) + 1;
    }
  });
  return {
    labels: Object.keys(statuses),
    data: Object.values(statuses),
  };
};

export const getMonthlyCount = (tenders) => {
  const months = {};
  tenders.forEach(t => {
    const month = dayjs(t.created_at).format('YYYY-MM');
    months[month] = (months[month] || 0) + 1;
  });

  const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).slice(-12);
  return {
    labels: sorted.map(([m]) => dayjs(m).format('MM/YYYY')),
    data: sorted.map(([, v]) => v),
  };
};

// ===== FILTER HELPERS =====
export const filterTenders = (tenders, filters) => {
  return tenders.filter(tender => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const searchable = [
        tender.client, tender.subject, tender.tender_number,
        tender.manager, tender.handler, tender.notes,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    if (filters.status && tender.status !== filters.status) return false;
    if (filters.field && tender.field !== filters.field) return false;
    if (filters.win_status && tender.win_status !== filters.win_status) return false;
    if (filters.priority && tender.priority !== filters.priority) return false;
    return true;
  });
};

// ===== EXPORT HELPERS =====
export const debounce = (fn, ms = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};
